/* eslint-disable @typescript-eslint/no-unused-vars */
import moment from 'moment';
import _pick from 'lodash/pick';
import { useRouter } from 'next/router';
import { useDebouncedCallback } from 'use-debounce';
import { useTranslation } from 'react-i18next';

import { useGetMe } from 'legacy/user/hooks';
import {
  useCreateEvent,
  useEditEvent,
  useGetEvent,
  useRequestToPublishEvent,
  useRequestToPublishEventWorkflow
} from 'legacy/events/hooks';

import { CURRENCY_CODES } from '../../util/constants';

import { isBounceWebview, postMessageToRN } from 'legacy/common/utils/webview';
import {
  WEBVIEW_CREATE_EVENT_ERROR,
  WEBVIEW_CREATE_EVENT_SUCCESS
} from 'legacy/common/constants/webview';
import { showError } from 'legacy/common/utils/toast';
import { Event } from 'legacy/common/types/models';
import { useEffect, useState } from 'react';
import { EventForHost } from 'legacy/events/types';
import { useGetGroup } from '../../groups/hooks';

interface useEventCreationProps {
  eventId?: string;
  orgId?: string;
  startDate?: string;
}

const DEBOUNCE_DELAY = 500;

const useEventCreation = (options?: useEventCreationProps) => {
  const { t } = useTranslation('events');
  const { orgId, startDate } = options ?? {};
  const { data: user } = useGetMe();

  const router = useRouter();

  const event = useGetEvent(options?.eventId);
  const { data: orgData } = useGetGroup({ groupId: orgId });

  const createEventMutation = useCreateEvent();
  const editEventMutation = useEditEvent();
  const requestToPublishEventMutation = useRequestToPublishEvent();
  const requestToPublishEventWorkflowMutation =
    useRequestToPublishEventWorkflow();

  const [shouldShowEventLoading, setShouldShowEventLoading] = useState(true);
  const [shouldRequestToPublish, setShouldRequestToPublish] = useState(false);
  const [requestToPublishFormId, setRequestToPublishFormId] = useState(null);
  const [requestToPublishWorkflowId, setRequestToPublishWorkflowId] =
    useState(null);

  useEffect(() => {
    const eventData = event?.data as EventForHost;
    if (eventData?.isDraft && eventData?.shouldRequestToPublish) {
      setShouldRequestToPublish(true);

      if (event.data.requestToPublishFormId) {
        setRequestToPublishFormId(event.data.requestToPublishFormId);
      }

      if (event.data.requestToPublishWorkflowId) {
        setRequestToPublishWorkflowId(event.data.requestToPublishWorkflowId);
      }
    }
  }, [event, shouldRequestToPublish]);

  const getInitialData = () => {
    const nextHour = moment().clone().add(1, 'hours').startOf('hour');

    // Use provided startDate (already normalized) or default to next hour
    const initialStartDate = startDate || nextHour.toISOString();

    return {
      title: '',
      startDate: initialStartDate,
      address: '',
      description: '',
      private: false,
      showGuests: true,
      ticketNotesRequest: null,
      googleForms: null,
      maxTicketsPerTransaction: 5,
      maxCapacity: undefined,
      metaPixelId: undefined,
      _id: undefined,
      isDraft: undefined,
      seriesId: undefined,
      groups: orgData && orgId ? [orgData] : undefined,
      pricing: {
        currency: CURRENCY_CODES[user?.country],
        tiers: [
          {
            name: t('pricingTiers.defaultTicketName'),
            price: 0 // default to a free event
          }
        ],
        transactionFeesPaidBy: 'guest'
      }
    };
  };

  const createDraft = async (values?: any) => {
    try {
      setShouldShowEventLoading(false);

      const eventData = { ...getInitialData(), ...values };

      const formattedData = {
        ...eventData,
        groups:
          eventData.groups?.map(
            (organization) => organization.id || organization
          ) || []
      };

      const newEvent = await createEventMutation.mutateAsync({
        event: formattedData
      });

      router.replace(`/events/create?eventId=${newEvent._id}`);

      return newEvent;
    } catch (error) {
      showError({
        title: 'Error saving event',
        message: error?.message
      });

      if (isBounceWebview(router.query)) {
        postMessageToRN(WEBVIEW_CREATE_EVENT_ERROR);
      }

      return null;
    }
  };

  /**
   * Pick relevant fields and format the data for the API
   */
  const formatUpdateForAPI = (event: Event) => {
    const filteredEvent = _pick(event, [
      'title',
      'description',
      'startDate',
      'endDate',
      'address',
      'coordinates',
      'private',
      'cohosts',
      'operators',
      'scanners',
      'photographers',
      'groups',
      'showGuests',
      'ticketNotesRequest',
      'googleForms',
      'video',
      'maxTicketsPerTransaction',
      'maxCapacity',
      'metaPixelId',
      'waiver',
      'chargeOrganizationFee',
      'momentsPermissions',
      'photo',
      'pricing',
      'isDraft',
      'guestQuestions'
    ]);

    const filteredPricing = _pick(filteredEvent.pricing, [
      'currency',
      'tax',
      'tips',
      'transactionFeesPaidBy',
      // Only include tiers if event is not created yet
      !event._id ? 'tiers' : undefined
    ]);

    return {
      ...filteredEvent,
      pricing: filteredPricing,
      cohosts: filteredEvent.cohosts?.map((cohost) => cohost._id) || [],
      groups:
        filteredEvent.groups?.map((organization) => organization.id) || [],
      operators: filteredEvent.operators?.map((operator) => operator._id) || [],
      scanners: filteredEvent.scanners?.map((scanner) => scanner._id) || [],
      photographers:
        filteredEvent.photographers?.map((photographer) => photographer._id) ||
        [],
      guestQuestions:
        filteredEvent.guestQuestions?.map(({ question }) => ({ question })) ||
        []
    };
  };

  const update = async (values) => {
    // If trying to update a non-existing event, create it instead.
    if (!options?.eventId) {
      createDraft(formatUpdateForAPI(values));
    } else {
      const formattedValues = formatUpdateForAPI(values);

      try {
        const response = await editEventMutation.mutateAsync({
          eventId: options?.eventId,
          event: formattedValues
        });
        if (response.isDraft && response.shouldRequestToPublish) {
          setShouldRequestToPublish(true);

          if (response.requestToPublishFormId) {
            setRequestToPublishFormId(response.requestToPublishFormId);
          }

          if (response.requestToPublishWorkflowId) {
            setRequestToPublishWorkflowId(response.requestToPublishWorkflowId);
          }
        }
      } catch (error) {
        showError({
          title: 'Error saving event',
          message: error?.message
        });
      }
    }
  };

  const requestPublication = async (data = {}) => {
    try {
      await requestToPublishEventMutation.mutateAsync({
        eventId: options.eventId,
        eventSeriesId: event.data.seriesId,
        ...data
      });
    } catch (error) {
      showError({
        title: 'Cannot request to publish event',
        message: error?.message
      });
    }
  };

  const requestPublicationWorkflow = async (data = {}) => {
    try {
      await requestToPublishEventWorkflowMutation.mutateAsync({
        eventId: options.eventId,
        eventSeriesId: event.data.seriesId,
        responseData: data
      });
    } catch (error) {
      showError({
        title: 'Cannot request to publish event',
        message: error?.message
      });
    }
  };

  const publish = async (values) => {
    try {
      await editEventMutation.mutateAsync({
        eventId: options.eventId,
        event: { ...formatUpdateForAPI(values), isDraft: false }
      });

      if (isBounceWebview(router.query)) {
        postMessageToRN(WEBVIEW_CREATE_EVENT_SUCCESS, {
          _id: options.eventId
        });
      }
    } catch (error) {
      showError({
        title: 'Cannot publish event',
        message: error?.message
      });
    }
  };

  const debouncedUpdate = useDebouncedCallback(update, DEBOUNCE_DELAY);

  return {
    eventId: options.eventId,
    event: event?.data,
    associationId: event?.data?.associationId,
    lastUpdated:
      editEventMutation.data?.updatedAt ||
      createEventMutation.data?.createdAt ||
      event?.data?.updatedAt,
    isLoading: createEventMutation.isLoading || editEventMutation.isLoading,
    isLoadingEvent: event?.isLoading,
    shouldShowEventLoading,
    shouldRequestToPublish,
    requestToPublishFormId,
    requestToPublishWorkflowId,
    initialValues: (event?.data || getInitialData()) as EventForHost,
    createDraft,
    update: debouncedUpdate,
    publish,
    requestPublication,
    requestPublicationWorkflow,
    refetchEvent: event?.refetch
  };
};

export default useEventCreation;
